import type { InputHTMLAttributes, KeyboardEvent, Ref } from "react";

import "./input.css";
import "./focus.css";
import { applyHomeEndKey } from "../../lib/home-end-caret";
import { cn } from "./cn";

export type InputSize = "sm" | "md" | "lg";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** Visual size. Renamed from the native numeric `size` attribute to avoid the collision. */
  inputSize?: InputSize;
  /** Marks the field invalid: red border + `aria-invalid` for assistive tech. */
  invalid?: boolean;
  ref?: Ref<HTMLInputElement>;
}

/**
 * Single-line text field. Token-driven; one focus-visible ring, reduced-motion aware. Pair it
 * with a `<label>` (or pass `aria-label`) for an accessible name.
 */
export function Input({
  inputSize = "md",
  invalid = false,
  className,
  onKeyDown,
  ref,
  ...rest
}: InputProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    // Home/End jump to the start/end of the value on every platform: macOS
    // leaves the field alone and scrolls the page instead. No-ops if the
    // consumer's handler already consumed the key.
    applyHomeEndKey(event);
  };

  return (
    <input
      ref={ref}
      onKeyDown={handleKeyDown}
      aria-invalid={invalid || undefined}
      className={cn(
        "rc-input",
        `rc-input--${inputSize}`,
        invalid && "rc-input--invalid",
        className,
      )}
      {...rest}
    />
  );
}
